import {useCallback, useState} from 'react';
import {channel} from 'redux-saga'
import {call, delay, select, takeLatest} from 'redux-saga/effects';

import {useIsFeedLoaded, useStoreFeed} from "../store/hooks";
import {actions, useSagaEffect} from '../store/sagas';
import {selectFeedEntry, selectFeedLastPost} from "../store/selectors";
import {useLoggedIn, useServerEffect, useSettings} from "../store";
import {normalizeCommunityName} from "../store/communites";
import {apiGet, apiPostJSON} from './client';

function* withRetries(key, saga) {
    yield actions.feedLoading(key);
    try {
        while (true) {
            const { failures } = yield select(selectFeedEntry(key)) || {};
            if (failures > 0) {
                yield delay(3000);
                if (failures >= 20) {
                    yield actions.feedLoadBailedOut(key);
                    return;
                }
            }
            try {
                yield call(saga);
                return;
            } catch (error) {
                yield actions.feedLoadFailed(key);
            }
        }
    } finally {
        yield actions.feedLoadDone(key);
    }
}

function* apiRequest(method, path, query) {
    if (method === 'POST') {
        return yield call(apiPostJSON, path, query);
    }
    const parts = [];
    for (const [key, val] of Object.entries(query)) {
        parts.push(`${key}=${encodeURIComponent(val)}`);
    }
    return yield call(apiGet, `${path}?${parts.join('&')}`);
}

function* loadFirstPage(key, method, path, query) {
    yield call(withRetries, key, function* () {
        const {posts, has_more_entries} = yield call(apiRequest, method, path, query);
        const more = typeof has_more_entries === 'boolean' ? has_more_entries : true;
        yield actions.feedLoaded(key, posts, more);
    });
}

function* loadNextPage(key, method, path, query) {
    const lastPost = yield select(selectFeedLastPost(key));
    if (!lastPost) {
        yield call(loadFirstPage, key, path);
        return;
    }
    yield call(withRetries, key, function* () {
        const {posts, has_more_entries} = yield call(apiRequest, method, path, {...query, from: lastPost.uuid});
        const more = typeof has_more_entries === 'boolean' ? has_more_entries : true;
        yield actions.feedTailLoaded(key, lastPost.id, posts, more);
    });
}

function* loadFeed(key, method, path, query, ch, shouldAttemptRestore = false) {
    yield takeLatest(ch, function* ({ type }) {
        switch (type) {
        case 'loadNextPage':
            yield call(loadNextPage, key, method, path, query);
            break;
        case 'refresh':
            yield actions.feedLoadReset(key);
            yield call(loadFirstPage, key, method, path, query);
            break;
        default:
            throw new Error(type);
        }
    });
    yield takeLatest('userChanged', function* () {
        yield call(loadFirstPage, key, method, path, query);
    });
    if (!shouldAttemptRestore) {
        yield call(loadFirstPage, key, method, path, query);
    }
}

export const useFeed = (feedbackCh, key, method, path, query, shouldAttemptRestore) => {
    const [actionCh] = useState(channel);
    useServerEffect(function* () {
        yield call(loadFirstPage, key, method, path, query);
    });
    const isFeedLoaded = useIsFeedLoaded(key);
    const [freshLoad, setFreshLoad] = useState(false);
    useSagaEffect(feedbackCh, useCallback(function* () {

        // if feed isn't loaded or if we shouldn't attempt restore, load fresh
        if (!isFeedLoaded || !shouldAttemptRestore) {
            setFreshLoad(true);
            yield actions.feedLoadReset(key);
            yield actions.feedAdUnitsCleared(key);
            yield call(loadFeed, key, method, path, query, actionCh);
        } else {
            // attempt to restore the feed from memory
            setFreshLoad(false);
            yield call(loadFeed, key, method, path, query, actionCh, true);
        }

    }, [key, method, path, JSON.stringify(query), actionCh]));
    const refresh = useCallback(() => actionCh.put({ type: 'refresh' }), [actionCh]);
    const loadNextPage = useCallback(() => actionCh.put({ type: 'loadNextPage' }), [actionCh]);
    const { posts, more, loading, failures, bailOut } = useStoreFeed(key);
    const okToLoad = !loading && !bailOut && (more || failures);

    return { posts, more, loading, refresh, loadNextPage: okToLoad ? loadNextPage : null, restoredFromMemory: !freshLoad };
};

export const useAltPosts = (feedbackCh, feed, community, page) => {
    const key = `c/${community}/alt:${feed}:${page}`;
    const path = `/api/v2/post/${feed}.json`;
    return useFeed(feedbackCh, key, 'GET', path, {community, page});
};

export const useSearch = (feedbackCh, query, community, sort, page) => {
    const key = `c/${community}/search:${query}:${page}:${sort}`;
    const path = `/api/v2/post/search.json`;
    return useFeed(feedbackCh, key, 'GET', path, {query, community, sort, page});
};

export const useFeedPosts = (feedbackCh, feedKey, community, sortBy, sort, feedId, trending_topics, from, shouldAttemptRestore) => {
    const loggedIn = useLoggedIn();
    const {subscribed, blocked_communities} = useSettings();
    community = normalizeCommunityName(community);
    sortBy += "v2";
    const method = community === 'home' && !loggedIn ? 'POST' : 'GET';
    const path = `/api/v2/post/${sortBy}.json`;
    const query = {community};
    if (community === 'home') {
        query.communities = loggedIn ? subscribed.length : subscribed;
    }
    if (from) {
        query.from = from;
    }
    if (feedId && community === 'win') {
        query.feedId = feedId;
    }
    if ((sortBy === 'top' || sortBy === 'topv2') && sort) {
        query.sort = sort;
    }
    if (!feedId && community === 'win') {
        query.isTrendingTopics = true;
        query.trendingTopics = JSON.stringify(trending_topics);
        query.blockedCommunities = JSON.stringify(blocked_communities);
    }

    return useFeed(feedbackCh, feedKey, method, path, query, shouldAttemptRestore);
};

export const useProfilePosts = (feedbackCh, username, sort, page, community) => {
    const key = `u/${username}:${sort}:${page}:${community}`;
    const path = '/api/v2/post/profile.json';
    return useFeed(feedbackCh, key, 'GET', path, {user: username, sort, page, community});
};

export const useQueuePosts = (feedbackCh, community, sort, page, comments, posts) => {
    const key = `queue/${community}:${sort}:${page}:${comments}:${posts}`;
    const path = '/api/v2/moderation/queue.json';
    return useFeed(feedbackCh, key, 'GET', path, {community, sort, page, comments, posts});
};
